import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Container } from 'react-bootstrap';
import GrupoDetalle from './GrupoDetalles';
import GrupoEmpresa from './empresa/Empresa';
import GrupoProveedor from './proveedor/GrupoProveedor';
import GrupoContacto from './contactos/GrupoContacto';
import ServicioAsignado from './servicio/ServicioAsignado';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    createEntity,
    getEmpresasByGrupo,
    getEntity,
    deleteProveedorConfigEnGrupo,
    getProveedoresByGrupo,
    reset,
    updateEntity,
} from './grupo-empresarial.reducer';
import { useAppDispatch, useAppSelector } from '../../store';
import { hasAnyAuthority, BASE_DE_CLIENTES_EDITAR_DATOS_DE_EMPRESAS_REQUIRED_AUTHORITIES } from '../../shared/authorization';
import PageAlert, { usePageAlert } from '../../commons/PageAlert';
import Confirm, { useConfirm } from '../../commons/Confirm';
import WindowHeader from '../../commons/WindowHeader';
import GrupoEditarModal from './GrupoEditarModal';
import EmpresaCrearModal from './empresa/EmpresaCrearModal';
import EmpresaEditarModal from './empresa/EmpresaEditarModal';
import { getEntities as getEmpresaServicios } from '../empresa-servicio.reducer';
import { getEntities as getContactos, deleteEntity as deleteContacto } from '../contacto.reducer';
import { getProveedores, getServicios } from '../empresa.reducer';
import { toInteger } from 'lodash';
import { findEmpresaEnGrupo } from './grupo-panel-title';
import {
    empresaIdFromProveedorRow,
    proveedorIdFromRow,
} from './proveedor/GrupoProveedorTable';

function etiquetaContacto(c: any): string {
    if (c?.contacto != null && String(c.contacto).trim() !== '') {
        return String(c.contacto).trim();
    }
    return c?.id != null ? `ID ${c.id}` : 'contacto';
}

function apiErrorMessage(error: unknown, fallback: string): string {
    if (error == null) return fallback;
    const err = error as { response?: { data?: unknown }; data?: unknown; message?: string };
    const candidates = [err.response?.data, err.data, error];
    for (const data of candidates) {
        if (data == null || typeof data !== 'object') continue;
        const body = data as { detail?: unknown; message?: unknown; title?: unknown };
        const detail = body.detail != null ? String(body.detail).trim() : '';
        if (detail) return detail;
        const message = body.message != null ? String(body.message).trim() : '';
        if (message) return message;
        const title = body.title != null ? String(body.title).trim() : '';
        if (title) return title;
    }
    if (typeof err.message === 'string' && err.message.trim()) return err.message.trim();
    return fallback;
}

function Grupo() {
    const navigate = useNavigate();
    const location = useLocation();

    const dispatch = useAppDispatch();
    const { alert, showToast } = usePageAlert();
    const deletingProveedor = useAppSelector(s => s.grupoEmpresarial.updating);
    const deletingContacto = useAppSelector(s => s.contacto.updating);
    const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

    const account = useAppSelector(state => state.authentication.account);
    const canEditGrupoEmpresas = hasAnyAuthority(
        account.authorities,
        BASE_DE_CLIENTES_EDITAR_DATOS_DE_EMPRESAS_REQUIRED_AUTHORITIES,
    );

    const { grupoId } = useParams<'grupoId'>();
    const { empresaId } = useParams<'empresaId'>();

    const loading = useAppSelector(state => state.grupoEmpresarial.loading);
    const grupoEmpresarialEntity = useAppSelector(state => state.grupoEmpresarial.entity) as any;

    const empresaServicios = useAppSelector(state => state.empresa.servicios) as any;
    const proveedoresEmpresa = useAppSelector(state => state.empresa.proveedores) as any[];
    const proveedoresGrupo = useAppSelector(state => state.grupoEmpresarial.proveedores) as any[];
    const tieneEmpresaSeleccionada = empresaId != null && empresaId !== 'new';
    const proveedores = tieneEmpresaSeleccionada ? proveedoresEmpresa : proveedoresGrupo;
    const contactos = useAppSelector(state => state.contacto.entities) as any;

    const empresas = useAppSelector(state => state.grupoEmpresarial.empresas) as any;

    const [suppressEditarGrupoFor403, setSuppressEditarGrupoFor403] = useState(false);
    const [suppressEmpresaCrearFor403, setSuppressEmpresaCrearFor403] = useState(false);
    const [suppressEmpresaEditarFor403, setSuppressEmpresaEditarFor403] = useState(false);
    const [contactosFilaExpandida, setContactosFilaExpandida] = useState(false);
    const [empresasFilaExpandida, setEmpresasFilaExpandida] = useState(false);
    const [proveedoresFilaExpandida, setProveedoresFilaExpandida] = useState(false);
    const [serviciosFilaExpandida, setServiciosFilaExpandida] = useState(false);
    const [alertaExitoProveedor, setAlertaExitoProveedor] = useState<string | null>(null);
    const [alertaExitoServicio, setAlertaExitoServicio] = useState<string | null>(null);
    const [alertaExitoContacto, setAlertaExitoContacto] = useState<string | null>(null);
    const [alertaErrorContacto, setAlertaErrorContacto] = useState<string | null>(null);
    const [alertaExitoEmpresa, setAlertaExitoEmpresa] = useState<string | null>(null);

    const isEditGrupoRoute =
        grupoId != null &&
        grupoId !== 'new' &&
        location.pathname === `/empresas/grupo/${grupoId}/edit`;

    const isNewEmpresaRoute =
        empresaId === 'new' && grupoId != null && grupoId !== 'new';

    const isEditEmpresaRoute =
        empresaId != null &&
        empresaId !== 'new' &&
        grupoId != null &&
        grupoId !== 'new' &&
        location.pathname === `/empresas/grupo/${grupoId}/empresa/${empresaId}/edit`;

    useEffect(() => {
        setContactosFilaExpandida(false);
        setEmpresasFilaExpandida(false);
        setProveedoresFilaExpandida(false);
        setServiciosFilaExpandida(false);
    }, [grupoId]);

    const toggleEmpresasFilaExpandida = () => {
        setEmpresasFilaExpandida(v => {
            const next = !v;
            if (next) setProveedoresFilaExpandida(false);
            return next;
        });
    };

    const toggleProveedoresFilaExpandida = () => {
        setProveedoresFilaExpandida(v => {
            const next = !v;
            if (next) setEmpresasFilaExpandida(false);
            return next;
        });
    };

    const toggleContactosFilaExpandida = () => {
        setContactosFilaExpandida(v => {
            const next = !v;
            if (next) setServiciosFilaExpandida(false);
            return next;
        });
    };

    const toggleServiciosFilaExpandida = () => {
        setServiciosFilaExpandida(v => {
            const next = !v;
            if (next) setContactosFilaExpandida(false);
            return next;
        });
    };

    useEffect(() => {
        const onSuppressEdit = () => setSuppressEditarGrupoFor403(true);
        const onSuppressEmpresaCrear = () => setSuppressEmpresaCrearFor403(true);
        const onSuppressEmpresaEditar = () => setSuppressEmpresaEditarFor403(true);
        const onForbiddenClosed = () => {
            setSuppressEditarGrupoFor403(false);
            setSuppressEmpresaCrearFor403(false);
            setSuppressEmpresaEditarFor403(false);
        };
        window.addEventListener('profase:suppress-grupo-editar-for-403', onSuppressEdit);
        window.addEventListener('profase:suppress-empresa-crear-for-403', onSuppressEmpresaCrear);
        window.addEventListener('profase:suppress-empresa-editar-for-403', onSuppressEmpresaEditar);
        window.addEventListener('profase:forbidden-modal-closed', onForbiddenClosed);
        return () => {
            window.removeEventListener('profase:suppress-grupo-editar-for-403', onSuppressEdit);
            window.removeEventListener('profase:suppress-empresa-crear-for-403', onSuppressEmpresaCrear);
            window.removeEventListener('profase:suppress-empresa-editar-for-403', onSuppressEmpresaEditar);
            window.removeEventListener('profase:forbidden-modal-closed', onForbiddenClosed);
        };
    }, []);

    useEffect(() => {
        if (!isEditGrupoRoute) setSuppressEditarGrupoFor403(false);
    }, [isEditGrupoRoute]);

    useEffect(() => {
        if (!isNewEmpresaRoute) setSuppressEmpresaCrearFor403(false);
    }, [isNewEmpresaRoute]);

    useEffect(() => {
        if (!isEditEmpresaRoute) setSuppressEmpresaEditarFor403(false);
    }, [isEditEmpresaRoute]);

    useEffect(() => {
        if (isNewEmpresaRoute && !canEditGrupoEmpresas) {
            navigate(`/empresas/grupo/${grupoId}`, { replace: true });
        }
    }, [isNewEmpresaRoute, canEditGrupoEmpresas, grupoId, navigate]);

    useEffect(() => {
        if (isEditEmpresaRoute && !canEditGrupoEmpresas) {
            navigate(`/empresas/grupo/${grupoId}/empresa/${empresaId}`, { replace: true });
        }
    }, [isEditEmpresaRoute, canEditGrupoEmpresas, grupoId, empresaId, navigate]);

    const navigateToEditGrupo = () => {
        navigate(`/empresas/grupo/${grupoId}/edit`, {
            state: {
                returnEmpresaId:
                    empresaId != null && empresaId !== 'new' ? empresaId : undefined,
            },
        });
    };

    const closeEditGrupo = () => {
        const ret = (location.state as { returnEmpresaId?: string } | null)?.returnEmpresaId;
        if (ret != null && ret !== '') {
            navigate(`/empresas/grupo/${grupoId}/empresa/${ret}`);
        } else {
            navigate(`/empresas/grupo/${grupoId}`);
        }
    };

    const onSavedEditGrupo = () => {
        /** El PUT de updateEntity ya deja `entity` en el store; no disparamos getEntity aquí para evitar loading=true y ocultar el toast. */
        showToast('success', 'Grupo actualizado con éxito.');
        navigate(`/empresas/grupo/${grupoId}`, { replace: true });
    };

    const closeEmpresaCrear = () => {
        navigate(`/empresas/grupo/${grupoId}`);
    };

    const onCreatedEmpresa = (newEmpresaId: number) => {
        setAlertaExitoProveedor(null);
        setAlertaExitoServicio(null);
        setAlertaExitoContacto(null);
        setAlertaErrorContacto(null);
        setAlertaExitoEmpresa('Empresa creada con éxito.');
        if (grupoId != null) {
            dispatch(getEmpresasByGrupo({ id: grupoId, page: 0, size: 99999 }));
        }
        navigate(`/empresas/grupo/${grupoId}/empresa/${newEmpresaId}`, { replace: true });
    };

    const closeEditEmpresa = () => {
        if (grupoId != null && empresaId != null && empresaId !== 'new') {
            navigate(`/empresas/grupo/${grupoId}/empresa/${empresaId}`, { replace: true });
        } else if (grupoId != null) {
            navigate(`/empresas/grupo/${grupoId}`, { replace: true });
        }
    };

    const onSavedEditEmpresa = () => {
        setAlertaExitoProveedor(null);
        setAlertaExitoServicio(null);
        setAlertaExitoContacto(null);
        setAlertaErrorContacto(null);
        setAlertaExitoEmpresa('Empresa actualizada con éxito.');
        if (grupoId != null) {
            dispatch(getEmpresasByGrupo({ id: grupoId, page: 0, size: 99999 }));
        }
        if (grupoId != null && empresaId != null && empresaId !== 'new') {
            navigate(`/empresas/grupo/${grupoId}/empresa/${empresaId}`, { replace: true });
        }
    };

    useEffect(() => {
        if (grupoId === 'new') {
            navigate('/empresas/grupo/new', { replace: true });
        }
    }, [grupoId, navigate]);

    const isNew = grupoId === undefined;

    useEffect(() => {
        if (isNew || grupoId === 'new') {
            return;
        }
        dispatch(getEntity(grupoId));
        dispatch(getEmpresasByGrupo({ id: grupoId, page: 0, size: 99999 }));
        dispatch(getContactos({
            grupoId,
            page: 0,
            size: 99999,
            sort: 'id,asc',
        }));
    }, [isNew, grupoId, dispatch]);


    useEffect(() => {
        if (isNew || grupoId == null || grupoId === 'new') return;
        if (tieneEmpresaSeleccionada) {
            dispatch(getServicios(toInteger(empresaId!)));
            dispatch(getProveedores(toInteger(empresaId!)));
        } else {
            dispatch(getProveedoresByGrupo(grupoId));
        }
    }, [isNew, grupoId, empresaId, tieneEmpresaSeleccionada, dispatch]);



    console.log('App loading');

    const seleccionarEmpresa = (empresa: any) => {
        navigate(`/empresas/grupo/${grupoId}/empresa/${empresa.id}`);
    }

    const deseleccionarEmpresa = (empresa: any) => {
        navigate(`/empresas/grupo/${grupoId}`);
    };

    const empresaSeleccionada = useMemo(
        () => findEmpresaEnGrupo(empresas, empresaId),
        [empresas, empresaId],
    );

    const puedeGestionarProveedores =
        canEditGrupoEmpresas && grupoId != null && grupoId !== '' && grupoId !== 'new';

    useEffect(() => {
        setConfirmLoading(deletingProveedor || deletingContacto);
    }, [deletingProveedor, deletingContacto, setConfirmLoading]);

    useEffect(() => {
        if (!alertaExitoProveedor) return;
        const timer = window.setTimeout(() => setAlertaExitoProveedor(null), 4000);
        return () => window.clearTimeout(timer);
    }, [alertaExitoProveedor]);

    useEffect(() => {
        if (!alertaExitoServicio) return;
        const timer = window.setTimeout(() => setAlertaExitoServicio(null), 4000);
        return () => window.clearTimeout(timer);
    }, [alertaExitoServicio]);

    useEffect(() => {
        if (!alertaExitoContacto) return;
        const timer = window.setTimeout(() => setAlertaExitoContacto(null), 4000);
        return () => window.clearTimeout(timer);
    }, [alertaExitoContacto]);

    useEffect(() => {
        if (!alertaExitoEmpresa) return;
        const timer = window.setTimeout(() => setAlertaExitoEmpresa(null), 4000);
        return () => window.clearTimeout(timer);
    }, [alertaExitoEmpresa]);

    const refetchProveedores = () => {
        if (tieneEmpresaSeleccionada) {
            dispatch(getProveedores(toInteger(empresaId!)));
        } else if (grupoId != null && grupoId !== 'new') {
            dispatch(getProveedoresByGrupo(grupoId));
        }
    };

    const refetchServicios = () => {
        if (tieneEmpresaSeleccionada) {
            dispatch(getServicios(toInteger(empresaId!)));
        }
    };

    const onServicioEliminado = () => {
        setAlertaExitoProveedor(null);
        setAlertaExitoContacto(null);
        setAlertaErrorContacto(null);
        setAlertaExitoServicio('Servicio asignado eliminado con éxito.');
        refetchServicios();
    };

    const confirmarEliminarContacto = async (c: any) => {
        if (c?.id == null || grupoId == null || grupoId === 'new') return;
        try {
            await dispatch(deleteContacto({ grupoId, id: c.id })).unwrap();
            dismiss();
            setAlertaExitoProveedor(null);
            setAlertaExitoServicio(null);
            setAlertaErrorContacto(null);
            setAlertaExitoContacto('Contacto eliminado con éxito.');
        } catch (err) {
            dismiss();
            setAlertaExitoContacto(null);
            setAlertaErrorContacto(apiErrorMessage(err, 'No se pudo eliminar el contacto.'));
        }
    };

    const solicitarEliminarContacto = (c: any) => {
        if (!canEditGrupoEmpresas || c?.id == null || grupoId == null || grupoId === 'new') return;
        const etiqueta = etiquetaContacto(c);
        setAlertaExitoContacto(null);
        setAlertaErrorContacto(null);
        ask(
            <>
                ¿Confirma eliminar el contacto <strong>{etiqueta}</strong>? Esta acción no se puede deshacer.
            </>,
            () => void confirmarEliminarContacto(c),
            {
                title: 'Eliminar contacto',
                confirmLabel: 'Eliminar',
                variant: 'danger',
            },
        );
    };

    const confirmarEliminarProveedor = async (row: any) => {
        if (grupoId == null || grupoId === '' || grupoId === 'new') return;
        const empresaIdRow = empresaIdFromProveedorRow(row);
        const proveedorId = proveedorIdFromRow(row);
        if (empresaIdRow == null || proveedorId == null) return;
        try {
            await dispatch(
                deleteProveedorConfigEnGrupo({
                    grupoId,
                    empresaId: empresaIdRow,
                    proveedorId,
                }),
            ).unwrap();
            dismiss();
            setAlertaExitoContacto(null);
            setAlertaErrorContacto(null);
            setAlertaExitoProveedor('Proveedor eliminado con éxito.');
            refetchProveedores();
        } catch {
            dismiss();
        }
    };

    const solicitarEliminarProveedor = (row: any) => {
        if (!puedeGestionarProveedores) return;
        if (empresaIdFromProveedorRow(row) == null || proveedorIdFromRow(row) == null) return;
        const etiqueta = String(
            row.proveedor?.nombre ?? row.alias ?? `ID ${proveedorIdFromRow(row)}`,
        ).trim();
        setAlertaExitoProveedor(null);
        setAlertaExitoContacto(null);
        setAlertaErrorContacto(null);
        ask(
            <>
                ¿Confirma eliminar la asignación de <strong>{etiqueta}</strong>? Esta acción no se puede
                deshacer.
            </>,
            () => void confirmarEliminarProveedor(row),
            {
                title: 'Eliminar proveedor',
                confirmLabel: 'Eliminar',
                variant: 'danger',
            },
        );
    };

    return (
        <div>
            {alert && (
                <div style={{ position: 'fixed', top: 72, right: 20, zIndex: 200000, maxWidth: 400 }}>
                    <PageAlert alert={alert} />
                </div>
            )}
            <GrupoEditarModal
                open={isEditGrupoRoute}
                forceHidden={suppressEditarGrupoFor403}
                onClose={closeEditGrupo}
                onSaved={onSavedEditGrupo}
                grupoId={grupoId}
            />
            <EmpresaCrearModal
                open={isNewEmpresaRoute && canEditGrupoEmpresas}
                forceHidden={suppressEmpresaCrearFor403}
                grupoId={grupoId}
                onClose={closeEmpresaCrear}
                onCreated={onCreatedEmpresa}
            />
            <EmpresaEditarModal
                open={isEditEmpresaRoute && canEditGrupoEmpresas}
                forceHidden={suppressEmpresaEditarFor403}
                grupoId={grupoId}
                empresaId={empresaId}
                onClose={closeEditEmpresa}
                onSaved={onSavedEditEmpresa}
            />
            {loading ? (
                <div className="p-4">Loading...</div>
            ) : (
            <div className="row g-0">
                <WindowHeader
                    title={grupoEmpresarialEntity.alias}
                    onClose={() => {
                        navigate(`/empresas`);
                    }}
                    onEditGrupo={
                        grupoId != null && grupoId !== 'new' && !isEditGrupoRoute
                            ? navigateToEditGrupo
                            : undefined
                    }
                    editGrupoDisabled={
                        grupoId != null &&
                        grupoId !== 'new' &&
                        !isEditGrupoRoute &&
                        !canEditGrupoEmpresas
                    }
                />
                {confirmConfig ||
                alertaExitoProveedor ||
                alertaExitoServicio ||
                alertaExitoContacto ||
                alertaErrorContacto ||
                alertaExitoEmpresa ? (
                    <div
                        className="col-12 border-bottom px-4 py-3 d-flex flex-column gap-2"
                        style={{ background: 'var(--crm-surface)' }}
                    >
                        {confirmConfig ? <Confirm config={confirmConfig} /> : null}
                        {alertaExitoProveedor ? (
                            <PageAlert
                                alert={{
                                    kind: 'toast',
                                    variant: 'success',
                                    message: alertaExitoProveedor,
                                    onDismiss: () => setAlertaExitoProveedor(null),
                                }}
                            />
                        ) : null}
                        {alertaExitoServicio ? (
                            <PageAlert
                                alert={{
                                    kind: 'toast',
                                    variant: 'success',
                                    message: alertaExitoServicio,
                                    onDismiss: () => setAlertaExitoServicio(null),
                                }}
                            />
                        ) : null}
                        {alertaExitoContacto ? (
                            <PageAlert
                                alert={{
                                    kind: 'toast',
                                    variant: 'success',
                                    message: alertaExitoContacto,
                                    onDismiss: () => setAlertaExitoContacto(null),
                                }}
                            />
                        ) : null}
                        {alertaErrorContacto ? (
                            <PageAlert
                                alert={{
                                    kind: 'toast',
                                    variant: 'error',
                                    message: alertaErrorContacto,
                                    onDismiss: () => setAlertaErrorContacto(null),
                                }}
                            />
                        ) : null}
                        {alertaExitoEmpresa ? (
                            <PageAlert
                                alert={{
                                    kind: 'toast',
                                    variant: 'success',
                                    message: alertaExitoEmpresa,
                                    onDismiss: () => setAlertaExitoEmpresa(null),
                                }}
                            />
                        ) : null}
                    </div>
                ) : null}
                {!proveedoresFilaExpandida ? (
                <div
                    className={
                        empresasFilaExpandida
                            ? 'col-12 border p-4'
                            : 'col-12 col-md-8 border p-4'
                    }
                    style={{ minWidth: 0, overflow: 'hidden' }}
                >
                    <GrupoEmpresa
                        grupoId={grupoId}
                        empresaId={empresaId}
                        empresas={empresas}
                        canEditGrupoEmpresas={canEditGrupoEmpresas}
                        filaExpandida={empresasFilaExpandida}
                        onToggleFilaExpandida={toggleEmpresasFilaExpandida}
                        seleccionarEmpresa={(empresa: any) => { seleccionarEmpresa(empresa); }}
                        deseleccionarEmpresa={(empresa: any) => { deseleccionarEmpresa(empresa); }}
                    />
                </div>
                ) : null}
                {!empresasFilaExpandida ? (
                <div
                    className={
                        proveedoresFilaExpandida
                            ? 'col-12 border p-4'
                            : 'col-12 col-md-4 border p-4'
                    }
                    style={{ minWidth: 0, overflow: 'hidden' }}
                >
                    <GrupoProveedor
                        proveedores={proveedores ?? []}
                        grupoId={grupoId}
                        empresaId={empresaId}
                        empresaSeleccionada={empresaSeleccionada}
                        canEditGrupoEmpresas={canEditGrupoEmpresas}
                        filaExpandida={proveedoresFilaExpandida}
                        onToggleFilaExpandida={toggleProveedoresFilaExpandida}
                        onProveedorAsignado={refetchProveedores}
                        onEliminarProveedor={
                            puedeGestionarProveedores ? solicitarEliminarProveedor : undefined
                        }
                    />
                </div>
                ) : null}
                {!serviciosFilaExpandida ? (
                <div
                    className={
                        contactosFilaExpandida
                            ? 'col-12 border p-4'
                            : 'col-12 col-md-6 border p-4'
                    }
                    style={{ minWidth: 0, overflow: 'hidden' }}
                >
                    {contactos && (
                        <GrupoContacto
                            grupoId={grupoId}
                            empresaId={empresaId}
                            empresaSeleccionada={empresaSeleccionada}
                            contactos={contactos}
                            canEditGrupoEmpresas={canEditGrupoEmpresas}
                            filaExpandida={contactosFilaExpandida}
                            onToggleFilaExpandida={toggleContactosFilaExpandida}
                            onEliminarContacto={
                                canEditGrupoEmpresas ? solicitarEliminarContacto : undefined
                            }
                        />
                    )}
                </div>
                ) : null}
                {!contactosFilaExpandida ? (
                <div
                    className={
                        serviciosFilaExpandida
                            ? 'col-12 border p-4'
                            : 'col-12 col-md-6 border p-4'
                    }
                    style={{ minWidth: 0, overflow: 'hidden' }}
                >
                    {empresaServicios && (
                        <ServicioAsignado
                            empresaServicios={empresaServicios}
                            grupoId={grupoId}
                            empresaId={empresaId}
                            empresaSeleccionada={empresaSeleccionada}
                            proveedores={proveedores}
                            empresasGrupo={empresas}
                            canEditGrupoEmpresas={canEditGrupoEmpresas}
                            filaExpandida={serviciosFilaExpandida}
                            onToggleFilaExpandida={toggleServiciosFilaExpandida}
                            onServicioAsignado={refetchServicios}
                            onServicioEliminado={onServicioEliminado}
                        />
                    )}
                </div>
                ) : null}
            </div >
            )}
        </div >
    );
}

export default Grupo;
